/**
 * Coordinates <-> address, for the signup address field.
 *
 * Mirrors HexagieApp `AddressPicker`'s `reverseGeocode` and
 * `LocationUtils.getCurrentLocation`: ask Google, take the first result's
 * formatted address, and fall back to the raw coordinates so the field is
 * never left empty after a successful fix.
 *
 * It deliberately goes to Google directly rather than through
 * `/addresses/from-location/`, which is a 500 on dev because the server-side
 * Google key is unset. The REST geocoder also needs no JS SDK, so the
 * location button keeps working even where the Places script fails to load.
 *
 * `fetch`, as the app uses, rather than `HexMap`'s axios wrapper: that
 * wrapper reaches the api client and pulls the whole HTTP layer in behind a
 * two-function helper.
 */

import { GOOGLE_MAP_KEY } from "../../utils/configs";

const GEOCODE_URL = "https://maps.googleapis.com/maps/api/geocode/json";

const asCoordinateLabel = (latitude, longitude) =>
  `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;

/** @returns {Promise<string>} a formatted address, never empty. */
export const reverseGeocode = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `${GEOCODE_URL}?latlng=${latitude},${longitude}&key=${GOOGLE_MAP_KEY}`
    );
    const data = await response.json();

    if (data?.status === "OK" && data.results?.length) {
      return data.results[0].formatted_address;
    }
  } catch {
    /* fall through to the coordinate label */
  }

  return asCoordinateLabel(latitude, longitude);
};

/** Browser geolocation, promisified. Rejects with a GeolocationPositionError. */
export const getCurrentPosition = (options = {}) =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("GEOLOCATION_UNSUPPORTED"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      reject,
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000, ...options }
    );
  });

/** Wording for the states the user can actually land in. */
export const locationErrorMessage = (error) => {
  if (error?.message === "GEOLOCATION_UNSUPPORTED") {
    return "This browser cannot share your location. Please type your address.";
  }

  // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
  if (error?.code === 1) {
    return "Location permission was denied. Please type your address.";
  }

  if (error?.code === 3) {
    return "Finding your location took too long. Please type your address.";
  }

  return "Could not find your location. Please type your address.";
};
